/**
 * SafetyEquipmentTab - Safety equipment configuration tab
 */

import React from 'react';
import { Field, Label, Description } from '../../../catalyst/fieldset';
import { Input } from '../../../catalyst/input';
import { Switch, Checkbox } from '../../../catalyst/checkbox';
import { devLog } from '../../../../utils/devLog';
import { parseCategory } from '../../../../utils/categoryHelpers';

const SafetyEquipmentTab = ({ vesselConfig, updateConfig }) => {
  // Equipment that may be fitted for operational reasons (not mandatory)
  const specialEquipment = [
    { value: 'winch_lifting_gear', label: 'Winch/Lifting Gear', help: 'Requires LOLER inspections and emergency stop familiarisation' },
    { value: 'gas_detection', label: 'Portable Gas Detection', help: 'For enclosed space entry' },
    { value: 'crane', label: 'Deck Crane', help: 'Lifting equipment requiring training and LOLER inspections' },
    { value: 'dynamic_positioning', label: 'Dynamic Positioning System', help: 'DP system requiring operator training' },
    { value: 'motion_compensated_gangway', label: 'Motion Compensated Gangway', help: 'CTV gangway system' },
    { value: 'fender_systems', label: 'Active Fender Systems', help: 'Deployable fender systems' }
  ];

  // LSA Equipment options
  const lsaEquipmentOptions = [
    { value: 'inflatable_lifejackets', label: 'Inflatable Lifejackets', help: 'Requires 2 additional spare lifejackets' },
    { value: 'immersion_suits', label: 'Immersion Suits', help: 'Thermal protective survival suits' },
    { value: 'thermal_protective_aids', label: 'Thermal Protective Aids (TPA)', help: 'For liferaft use' },
    { value: 'line_throwing_apparatus', label: 'Line Throwing Apparatus', help: 'Rocket line or similar' },
    { value: 'scramble_nets', label: 'Scramble Nets', help: 'For recovery of persons from water' },
    { value: 'jason_cradle', label: 'Jason\'s Cradle', help: 'MOB recovery equipment' },
    { value: 'recovery_basket', label: 'Recovery Basket/Davit', help: 'Personnel recovery system' }
  ];

  // Calculate if liferafts are required based on area category
  const category = parseCategory(vesselConfig.category);
  const liferaftsRequired = vesselConfig.category !== '' && category <= 3;

  // Determine if immersion suits are mandatory based on vessel type and category
  const vesselType = vesselConfig.vesselType || vesselConfig.type;
  
  // Debug logging
  devLog('[SafetyEquipmentTab] Checking immersion suits for:', {
    vesselType,
    category,
    operatingStatus: vesselConfig.operatingStatus
  });
  
  let immersionSuitsMandatory = false;
  let immersionSuitsReason = '';
  
  // Check all qualifying conditions for immersion suits
  const catStr = String(vesselConfig.category || '').toLowerCase();
  if (vesselConfig.operatingArea === 'inland' || catStr === 'iww') {
    // Inland waterways - never required
    immersionSuitsMandatory = false;
  } else if (vesselType === 'police') {
    // Police boats (seagoing) - always required
    immersionSuitsMandatory = true;
    immersionSuitsReason = 'Required for all police boats (WBC3 Annex 3)';
  } else if (vesselType === 'pilot') {
    // Pilot boats - conditional
    if (category <= 5) {
      immersionSuitsMandatory = true;
      immersionSuitsReason = 'Required for Cat 0-5 pilot boats (WBC3 Section 27.2.3)';
    } else if (category === 6 && vesselConfig.operatingHours !== 'daylight_only') {
      immersionSuitsMandatory = true;
      immersionSuitsReason = 'Required for Cat 6 pilot boats with night operations';
    }
  } else if (vesselType === 'safety_standby') {
    // Safety/Standby boats - required for rescue operations
    if (category <= 5) {
      immersionSuitsMandatory = true;
      immersionSuitsReason = 'Required for safety/standby boats Cat 0-5 (rescue operations)';
    } else {
      immersionSuitsReason = 'Recommended for safety/standby boats (rescue capability)';
    }
  } else if (category <= 5) {
    // Standard vessels Cat 0-5 - optional (can choose immersion suits OR insulated lifejackets)
    // Will show as optional equipment they can select
    immersionSuitsReason = 'Optional for Cat 0-5 vessels (alternative to insulated lifejackets)';
  }
  
  // Ensure lsaEquipment array exists
  if (!vesselConfig.lsaEquipment) {
    vesselConfig.lsaEquipment = [];
  }
  
  // Auto-add immersion suits if mandatory and not already present
  React.useEffect(() => {
    if (immersionSuitsMandatory && !vesselConfig.lsaEquipment?.includes('immersion_suits')) {
      updateConfig({ 
        lsaEquipment: [...(vesselConfig.lsaEquipment || []), 'immersion_suits']
      });
    }
  }, [immersionSuitsMandatory]);

  // Defensive cleanup of inflatable flags when lifejacketType is foam
  React.useEffect(() => {
    if (vesselConfig.lifejacketType === 'foam') {
      const needsCleanup = 
        vesselConfig.hasInflatableLifejackets === true ||
        (vesselConfig.lsaEquipment && vesselConfig.lsaEquipment.includes('inflatable_lifejackets'));
      
      if (needsCleanup) {
        const updates = {};
        
        if (vesselConfig.hasInflatableLifejackets === true) {
          updates.hasInflatableLifejackets = false;
        }
        
        if (vesselConfig.lsaEquipment && vesselConfig.lsaEquipment.includes('inflatable_lifejackets')) {
          const cleanedLsaEquipment = vesselConfig.lsaEquipment.filter(item => item !== 'inflatable_lifejackets');
          updates.lsaEquipment = cleanedLsaEquipment;
        }

        updateConfig(updates);
      }
    }
  }, [vesselConfig.lifejacketType, vesselConfig.hasInflatableLifejackets, vesselConfig.lsaEquipment]);

  return (
    <div className="space-y-6">
      {/* Mandatory Equipment Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900">Mandatory Safety Equipment</h3>
        <p className="mt-1 text-sm text-blue-700">
          The following equipment is automatically included based on your vessel configuration:
        </p>
        <ul className="mt-2 text-sm text-blue-700 list-disc list-inside">
          <li>Fire extinguishers, buckets, and detection systems</li>
          <li>Lifejackets for 100% of persons on board</li>
          <li>Navigation lights, shapes, and sound signals</li>
          <li>VHF Radio with DSC and Portable VHF</li>
          <li>Magnetic compass</li>
          {liferaftsRequired && <li>Liferafts (Area Category 0-3 requirement)</li>}
          {vesselConfig.category <= 2 && <li>EPIRB and SART (Area Category 0-2 requirement)</li>}
          {vesselConfig.enginePosition === 'below_deck' && <li>Fixed fire-fighting system (engine below deck)</li>}
          {immersionSuitsMandatory && <li>Immersion Suits - {immersionSuitsReason}</li>}
        </ul>
      </div>

      {/* Equipment Variations */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Equipment Type Selection</h3>
        <div className="space-y-3">
          <Field>
            <Label>Lifejacket Type</Label>
            <select
              value={vesselConfig.lifejacketType}
              onChange={(e) => {
                const newType = e.target.value;
                const updates = { lifejacketType: newType };
                
                // Clean up inflatable flags when switching to foam
                if (newType === 'foam') {
                  updates.hasInflatableLifejackets = false;
                  
                  // Remove 'inflatable_lifejackets' from lsaEquipment array if present
                  const currentLsaEquipment = vesselConfig.lsaEquipment || [];
                  const cleanedLsaEquipment = currentLsaEquipment.filter(item => item !== 'inflatable_lifejackets');
                  if (cleanedLsaEquipment.length !== currentLsaEquipment.length) {
                    updates.lsaEquipment = cleanedLsaEquipment;
                  }
                }
                
                updateConfig(updates);
              }}
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
            >
              <option value="foam">Foam (Inherent Buoyancy)</option>
              <option value="inflatable">Inflatable</option>
            </select>
            <Description>Affects maintenance requirements and inspection frequency</Description>
          </Field>

          {/* Liferaft configuration if required */}
          {liferaftsRequired && (
            <>
              <Field>
                <Label>Liferaft Capacity (persons)</Label>
                <Input
                  type="number"
                  value={vesselConfig.liferaft_capacity}
                  onChange={(e) => updateConfig({ liferaft_capacity: e.target.value })}
                  placeholder="e.g., 12"
                />
                <Description>Total capacity of all liferafts fitted</Description>
              </Field>

              <Field>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>HRU (Hydrostatic Release Unit) Fitted</Label>
                    <Description>Automatic release mechanism for liferaft</Description>
                  </div>
                  <Switch
                    checked={vesselConfig.hru_fitted}
                    onChange={(checked) => updateConfig({ hru_fitted: checked })}
                  />
                </div>
              </Field>

              {vesselConfig.hru_fitted && (
                <Field>
                  <Label>HRU Type</Label>
                  <select
                    value={vesselConfig.hruType}
                    onChange={(e) => updateConfig({ hruType: e.target.value })}
                    className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
                  >
                    <option value="disposable">Disposable (2-4 year replacement)</option>
                    <option value="serviceable">Serviceable (Annual service)</option>
                  </select>
                </Field>
              )}
            </>
          )}
        </div>
      </div>

      {/* Optional Boats */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Optional Boats (not required by regulations)</h3>
        <div className="space-y-3">
          <Field>
            <div className="flex items-center justify-between">
              <div>
                <Label>Lifeboat Fitted</Label>
                <Description>Vessel has lifeboats (requires weekly engine runs)</Description>
              </div>
              <Switch
                checked={vesselConfig.lifeboat_fitted}
                onChange={(checked) => updateConfig({ lifeboat_fitted: checked })}
              />
            </div>
          </Field>

          <Field>
            <div className="flex items-center justify-between">
              <div>
                <Label>Rescue Boat Fitted</Label>
                <Description>Fast rescue boats (requires weekly engine runs)</Description>
              </div>
              <Switch
                checked={vesselConfig.rescue_boat_fitted}
                onChange={(checked) => updateConfig({ rescue_boat_fitted: checked })}
              />
            </div>
          </Field>
        </div>
      </div>

      {/* LSA Equipment */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">LSA Equipment</h3>
        {immersionSuitsReason && (
          <p className="text-sm text-gray-600 mb-3">{immersionSuitsReason}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {lsaEquipmentOptions.map(eq => {
            const isChecked = vesselConfig.lsaEquipment?.includes(eq.value) || false;
            const isMandatory = eq.value === 'immersion_suits' && immersionSuitsMandatory;
            
            return (
              <label key={eq.value} className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${
                isMandatory ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
              }`}>
                <Checkbox
                  checked={isChecked}
                  disabled={isMandatory}
                  onChange={(checked) => {
                    if (isMandatory) return; // Don't allow unchecking mandatory items
                    const currentEq = vesselConfig.lsaEquipment || [];
                    const newEq = checked 
                      ? [...currentEq, eq.value]
                      : currentEq.filter(e => e !== eq.value);
                    updateConfig({ lsaEquipment: newEq });
                  }}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{eq.label}</span>
                    {isMandatory && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Mandatory</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{eq.help}</p>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Special Equipment */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Special Equipment (select all that apply)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {specialEquipment.map(eq => (
            <label key={eq.value} className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
              <Checkbox
                checked={vesselConfig.specialEquipment?.includes(eq.value) || false}
                onChange={(checked) => {
                  const currentEq = vesselConfig.specialEquipment || [];
                  const newEq = checked 
                    ? [...currentEq, eq.value]
                    : currentEq.filter(e => e !== eq.value);
                  updateConfig({ specialEquipment: newEq });
                }}
                className="mt-0.5"
              />
              <div className="flex-1">
                <p className="font-medium text-gray-900 text-sm">{eq.label}</p>
                <p className="text-xs text-gray-600">{eq.help}</p>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SafetyEquipmentTab;